import React, { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useNostr } from '@/hooks/useNostr';
import { useQuery } from '@tanstack/react-query';
import { Tweet } from '@/components/tweet/Tweet';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, TrendingUp, Sparkles } from 'lucide-react';

export function ExplorePage() {
  useSeoMeta({
    title: 'Explore / Twittstr',
    description: 'Find conversations, trending tags, and new creators across decentralized relays.',
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';
  const { nostr } = useNostr();

  const [searchVal, setSearchVal] = useState(queryParam);

  // Sync state with search queries
  useEffect(() => {
    setSearchVal(queryParam);
  }, [queryParam]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchVal.trim()) return;
    navigate(`/explore?q=${encodeURIComponent(searchVal.trim())}`);
  };

  // Query search notes
  const { data: searchResults = [], isLoading, isFetching } = useQuery({
    queryKey: ['explore-search', queryParam],
    queryFn: async () => {
      if (!nostr) return [];
      if (!queryParam) {
        // Fallback search to random hashtag trend
        return nostr.query([{ kinds: [1], limit: 30 }]);
      }
      
      const hashtags = queryParam.startsWith('#') ? [queryParam.slice(1).toLowerCase()] : undefined;
      const search = hashtags ? undefined : queryParam;

      const filter: any = { kinds: [1], limit: 35 };
      if (hashtags) filter['#t'] = hashtags;
      if (search) filter['search'] = search;

      try {
        return await nostr.query([filter]);
      } catch {
        // Relays without search filter support fallback to general feed
        return await nostr.query([{ kinds: [1], limit: 20 }]);
      }
    },
    enabled: !!nostr,
  });

  const trends = [
    { rank: '1 · Trending', title: 'Nostr protocol', posts: '15.8K posts' },
    { rank: '2 · Technology', title: 'Web3 & AI development', posts: '122.3K posts' },
    { rank: '3 · Finance', title: 'Taproot Bitcoin zaps', posts: '87.1K posts' },
    { rank: '4 · Art', title: 'Blossom server host', posts: '9.4K posts' },
  ];

  return (
    <div className="flex flex-col w-full min-h-screen bg-background">
      {/* Search Bar header */}
      <header className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border p-3 flex flex-col gap-3">
        <form onSubmit={handleSearchSubmit} className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground size-4" />
          <Input
            type="text"
            placeholder="Search Twittstr"
            className="w-full pl-11 bg-secondary/40 border-none rounded-full h-11 focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-sky-500 text-[15px]"
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
          />
        </form>
      </header>

      {/* Explore Contents list */}
      <div className="flex flex-col select-none">
        
        {/* Render Trends section if no active search queries */}
        {!queryParam && (
          <div className="flex flex-col border-b border-border/80 pb-3">
            <div className="p-4 flex items-center justify-between">
              <span className="font-extrabold text-xl flex items-center gap-1.5">
                Trends for you <Sparkles className="size-4.5 text-sky-500 fill-sky-500" />
              </span>
            </div>
            
            <div className="flex flex-col">
              {trends.map((t, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setSearchVal(t.title);
                    navigate(`/explore?q=${encodeURIComponent(t.title)}`);
                  }}
                  className="text-left w-full py-3.5 px-4 hover:bg-secondary/40 cursor-pointer transition-colors leading-snug flex flex-col gap-0.5"
                >
                  <span className="text-xs text-muted-foreground">{t.rank}</span>
                  <span className="font-extrabold text-base">{t.title}</span>
                  <span className="text-xs text-muted-foreground">{t.posts}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Explore / Query Feed results header */}
        <div className="p-4 border-b border-border/40">
          <span className="font-extrabold text-lg flex items-center gap-2">
            <TrendingUp className="size-5 text-sky-500" />
            {queryParam ? `Search results for "${queryParam}"` : 'Recommended Posts'}
          </span>
        </div>

        {/* Results Stream */}
        <div className="flex flex-col divide-y divide-border">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="p-4 flex gap-3 border-b border-border animate-pulse">
                <Skeleton className="size-10 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/4 rounded-md" />
                  <Skeleton className="h-4 w-full rounded-md" />
                </div>
              </div>
            ))
          ) : searchResults.length > 0 ? (
            searchResults.map((event) => (
              <Tweet key={event.id} event={event} />
            ))
          ) : (
            <div className="p-12 text-center text-muted-foreground text-sm leading-normal">
              No matching posts found for this search. Please try checking your query terms or searching for popular hashtags like #nostr or #bitcoin!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExplorePage;
